import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  CreditCard,
  Wallet,
  Building2,
  Plus,
  History,
  TrendingUp,
  TrendingDown,
  Loader2,
  ShoppingCart,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgentBilling() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [creditAmount, setCreditAmount] = useState(10);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    if (token) {
      fetchDashboardData();
      fetchTransactions();
    }
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/agent/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/api/agent/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handlePurchaseCredits = async () => {
    if (creditAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setPurchasing(true);
      const response = await axios.post(`${API}/api/agent/purchase-credits`, 
        { amount: creditAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Credits purchased successfully!');
        setShowBuyCredits(false);
        fetchDashboardData();
        fetchTransactions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate purchase');
    } finally {
      setPurchasing(false);
    }
  };

  const pricePerCredit = 15; // £15 per credit
  const totalPrice = creditAmount * pricePerCredit;

  const personalCredits = dashboardData?.personal_credits || 0;
  const allocatedCredits = dashboardData?.allocated_credits || 0;
  const allocatedBreakdown = dashboardData?.allocated_credits_breakdown || [];
  const totalCredits = personalCredits + allocatedCredits;

  return (
    <DashboardLayout role="agent">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Credits & Billing</h1>
            <p className="text-muted-foreground">Manage your credits and view transaction history</p>
          </div>
          <Button 
            onClick={() => setShowBuyCredits(true)} 
            className="bg-lime-500 hover:bg-lime-600"
            data-testid="buy-credits-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Buy Credits
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
          </div>
        ) : (
          <>
            {/* Credits Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-lime-100 dark:bg-lime-900/30 rounded-xl">
                      <Wallet className="w-6 h-6 text-lime-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Personal Credits</p>
                      <p className="text-3xl font-bold text-lime-600">{personalCredits}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">Can be used for any university</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Allocated Credits</p>
                      <p className="text-3xl font-bold text-blue-600">{allocatedCredits}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    From {allocatedBreakdown.length} organization{allocatedBreakdown.length !== 1 ? 's' : ''}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                      <CreditCard className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Available</p>
                      <p className="text-3xl font-bold text-purple-600">{totalCredits}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">Combined credits balance</p>
                </CardContent>
              </Card>
            </div>

            {/* Allocated Credits Breakdown */}
            {allocatedBreakdown.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Allocated Credits Breakdown
                  </CardTitle>
                  <CardDescription>Credits allocated by organizations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {allocatedBreakdown.map((alloc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Building2 className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="font-medium">{alloc.org_name || 'Organization'}</p>
                            <p className="text-xs text-muted-foreground">
                              Allocated on {new Date(alloc.allocated_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="bg-blue-50 text-blue-600 text-lg px-3 py-1">
                          {alloc.credits || alloc.amount || 0}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pricing Info */}
            <Card className="bg-gradient-to-r from-lime-50 to-emerald-50 dark:from-lime-900/20 dark:to-emerald-900/20 border-lime-200 dark:border-lime-800">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <h3 className="text-lg font-semibold">Need More Credits?</h3>
                    <p className="text-muted-foreground">Purchase personal credits at £{pricePerCredit} per credit</p>
                  </div>
                  <Button onClick={() => setShowBuyCredits(true)} className="bg-lime-500 hover:bg-lime-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Buy Credits Now
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Transaction History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-500" />
                  Transaction History
                </CardTitle>
                <CardDescription>Your credit purchases and usage</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((txn) => (
                        <TableRow key={txn.id}>
                          <TableCell>
                            {txn.type === 'purchase' ? (
                              <div className="flex items-center gap-2 text-green-600">
                                <ArrowUpRight className="w-4 h-4" />
                                Purchase
                              </div>
                            ) : txn.type === 'usage' ? (
                              <div className="flex items-center gap-2 text-red-600">
                                <ArrowDownRight className="w-4 h-4" />
                                Usage
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-blue-600">
                                <Building2 className="w-4 h-4" />
                                Allocation
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{txn.description || 'Credit transaction'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={txn.amount > 0 ? 'text-green-600' : 'text-red-600'}>
                              {txn.amount > 0 ? '+' : ''}{txn.amount}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(txn.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={txn.status === 'completed' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}>
                              {txn.status === 'completed' ? (
                                <><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</>
                              ) : (
                                <><Clock className="w-3 h-3 mr-1" /> Pending</>
                              )}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Buy Credits Dialog */}
        <Dialog open={showBuyCredits} onOpenChange={setShowBuyCredits}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-lime-500" />
                Buy Credits
              </DialogTitle>
              <DialogDescription>
                Purchase personal credits for your account
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Number of Credits</Label>
                <Input
                  id="amount"
                  type="number"
                  min="1"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                  data-testid="credit-amount-input"
                />
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Price per credit</span>
                  <span>£{pricePerCredit}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Quantity</span>
                  <span>{creditAmount} credits</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-lime-600">£{totalPrice}</span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Personal credits can be used to create applications for any university in the system.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBuyCredits(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePurchaseCredits} 
                className="bg-lime-500 hover:bg-lime-600"
                disabled={purchasing || creditAmount <= 0}
                data-testid="confirm-purchase-btn"
              >
                {purchasing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay £{totalPrice}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
